import React, { FC } from "react";
import { Button, Checkbox, Form, Input, Radio } from "antd";
import { FormComponentProps } from "antd/lib/form";
import {
	ITrainingCourse,
	TrainingCourseEnrollment,
} from "src/@types/ImpiricaPlatformApi";
import TextArea from "antd/lib/input/TextArea";
import CourseThumbnail from "./CourseThumbnail";
import RadioGroup from "antd/lib/radio/group";
import CourseEnrollmentList from "./CourseEnrollmentList";

interface IProps extends FormComponentProps {
	value: ITrainingCourse;
	onChange?: (value: ITrainingCourse) => void;
}

const AddEditCourseForm: FC<IProps> = ({ onChange, form, value }) => {
	const isNew = value.id <= 0;
	const buttonText = isNew ? "Add Course" : "Update Course";
	const [disableSubmit, setDisableSubmit] = React.useState<boolean>(false);

	const _isBase64EncodedNumberOrEmpty = (input: string, fieldName: string) => {
		try {
			if (!input) {
				return undefined;
			}

			const decoded = atob(input);
			if (!Number(decoded)) {
				return `${fieldName} is not a properly encoded number`;
			}
			return undefined;
		} catch (e) {
			return `${fieldName} is not a properly formatted string`;
		}
	};

	const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setDisableSubmit(true);
		form.validateFieldsAndScroll((err, values) => {
			if (!err) {
				const update = {
					...value,
					...values,
					enrollments: values.enrollments.map((e: number) => {
						return new TrainingCourseEnrollment({
							courseId: value.id,
							productId: e,
						});
					}),
				};

				if (onChange) {
					onChange(update);
				}
			} else {
				setDisableSubmit(false);
			}
		});
	};

	return (
		<Form onSubmit={onSubmit}>
			{/* Id */}
			<div className="row">
				<div className="col">
					<Form.Item className="form-group">
						{form.getFieldDecorator("id", {
							initialValue: value.id,
							rules: [{ required: true }],
						})(<Input type="hidden" />)}
					</Form.Item>
				</div>
			</div>

			{/* contains 2 column layout for title, desc and thumbnail */}
			<div className="row">
				<div className="col-7">
					<Form.Item label="Title" className="form-group">
						{form.getFieldDecorator("title", {
							initialValue: value.title,
							rules: [
								{
									required: true,
									message: "Title is required",
								},
								{
									max: 100,
									message: "Title must be less than 100 characters",
								},
							],
						})(<Input placeholder="Enter a title" />)}
					</Form.Item>
					<Form.Item label="Description" className="form-group">
						{form.getFieldDecorator("description", {
							initialValue: value.description,
							rules: [
								{
									max: 255,
									message: "Description must be less than 255 characters",
								},
							],
						})(<TextArea placeholder="Enter a description" rows={3} />)}
					</Form.Item>
				</div>
				<div className="col-5">
					<Form.Item label="Thumbnail Image" className="form-group">
						{form.getFieldDecorator("imageUrl", {
							initialValue: value.imageUrl,
							rules: [
								{
									max: 65535,
									message: "Image URL must be less than 65535 characters",
								},
							],
						})(<CourseThumbnail />)}
					</Form.Item>
				</div>
			</div>

			{/* Active / InActive */}
			<div className="row">
				<div className="col">
					<Form.Item className="form-group">
						{form.getFieldDecorator("isActive", {
							initialValue: value.isActive,
							rules: [
								{
									required: true,
									message: "Active is required",
								},
							],
						})(
							<RadioGroup>
								<Radio value={true}>Active</Radio>
								<Radio value={false}>Inactive</Radio>
							</RadioGroup>
						)}
					</Form.Item>
				</div>
			</div>

			{/* Enrollments */}
			<div className="row">
				<div className="col">
					<Form.Item
						label="Course Enrollments"
						className="form-group"
						hasFeedback={false}
					>
						{form.getFieldDecorator("autoEnrollAll", {
							initialValue: value.autoEnrollAll,
							valuePropName: "checked",
						})(<Checkbox>Enroll All Users</Checkbox>)}
					</Form.Item>
					<Form.Item
						className="form-group"
						help="Automatically enroll in this course for these certifications"
					>
						{form.getFieldDecorator("enrollments", {
							initialValue: value.enrollments
								? value.enrollments.map((e) => e.productId)
								: [],
						})(
							<CourseEnrollmentList
								style={{
									display: form.getFieldValue("autoEnrollAll")
										? "none"
										: "block",
								}}
							/>
						)}
					</Form.Item>
				</div>
			</div>

			{/* Project */}
			<div className="row">
				<div className="col-md-4">
					<Form.Item
						label="Linked Project Id"
						className="form-group"
						help="You can find the Project Id in the Url of the spotlightr website"
					>
						{form.getFieldDecorator("projectId", {
							initialValue: value.projectId,
							rules: [
								{
									required: true,
									message: "Project Id is required",
								},
								{
									validator: (rule, value, callback) => {
										const validation = _isBase64EncodedNumberOrEmpty(
											value,
											"Project Id"
										);
										callback(validation);
									},
								},
							],
						})(<Input />)}
					</Form.Item>
				</div>
			</div>

			{/* Gallery */}
			<div className="row">
				<div className="col-md-4">
					<Form.Item
						label="Linked Gallery Id"
						className="form-group"
						help="You can find the Gallery Id in the Url of the spotlightr website"
					>
						{form.getFieldDecorator("galleryId", {
							initialValue: value.galleryId,
							rules: [
								{
									required: true,
									message: "Gallery Id is required",
								},
								{
									validator: (rule, value, callback) => {
										const validation = _isBase64EncodedNumberOrEmpty(
											value,
											"Gallery Id"
										);
										callback(validation);
									},
								},
							],
						})(<Input />)}
					</Form.Item>
				</div>
			</div>

			{/* Quiz */}
			<div className="row">
				<div className="col-md-4">
					<Form.Item
						label="Linked Quiz Id"
						className="form-group"
						help="You can find the Quiz Id in the Url of the spotlightr website"
					>
						{form.getFieldDecorator("quizId", {
							initialValue: value.quizId,
							rules: [
								{
									validator: (rule, value, callback) => {
										const validation = _isBase64EncodedNumberOrEmpty(
											value,
											"Quiz Id"
										);
										callback(validation);
									},
								},
							],
						})(<Input />)}
					</Form.Item>
				</div>
			</div>

			{/* WebHook */}
			<div className="row">
				<div className="col">
					<Form.Item label="Web Hook" className="form-group">
						{form.getFieldDecorator("webHookUrl", {
							initialValue: value.webHookUrl,
							rules: [
								{
									max: 65535,
									message: "Web Hook URL must be less than 65535 characters",
								},
								{
									type: "url",
								},
							],
						})(<Input />)}
					</Form.Item>
				</div>
			</div>

			{/* Submit Button */}
			<div className="row">
				<div className="col">
					<Form.Item>
						<Button
							data-testid="add-edit-course-form-submit"
							type="primary"
							htmlType="submit"
							className="login-form-button"
							disabled={disableSubmit}
						>
							{buttonText}
						</Button>
					</Form.Item>
				</div>
			</div>
		</Form>
	);
};

export default Form.create<IProps>()(AddEditCourseForm);
